/**
 * StationImpostorBaker — renders the station GLB model offscreen with Three.js
 * and produces a 2D canvas that can be converted to a PixiJS texture.
 *
 * Called once at boot time. The result is cached and reused for every Station
 * entity in the game world, replacing the old SVG-based rendering.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Texture as PixiTexture } from 'pixi.js';

const STATION_MODEL_PATH = '/assets/3d/test/gateway_lunar_space_station.glb';
const IMPOSTOR_SIZE = 1024; // px — square texture

/** Cached impostor canvases keyed by tint hex (e.g. '20a5a6'). */
const impostorCache: Map<string, HTMLCanvasElement> = new Map();

/** PixiJS texture cache so we don't create duplicates. */
const pixiTextureCache: Map<string, PixiTexture> = new Map();

/** The raw GLB scene, loaded once. */
let glbScene: THREE.Group | null = null;
let glbPromise: Promise<THREE.Group | null> | null = null;

function loadGLB(): Promise<THREE.Group | null> {
  if (glbScene) return Promise.resolve(glbScene);
  if (glbPromise) return glbPromise;

  glbPromise = new Promise((resolve) => {
    const loader = new GLTFLoader();
    loader.load(
      STATION_MODEL_PATH,
      (gltf) => {
        glbScene = gltf.scene;
        resolve(glbScene);
      },
      undefined,
      (err) => {
        console.warn('[StationImpostor] Failed to load GLB:', err);
        resolve(null);
      },
    );
  });
  return glbPromise;
}

/**
 * Render the station model to an offscreen canvas.
 * @param tintHex - hex color string without '#' (e.g. '20a5a6') used for rim light tint.
 *                  Defaults to the ASTRA teal.
 */
export async function bakeStationImpostor(tintHex: string = '20a5a6'): Promise<HTMLCanvasElement | null> {
  if (impostorCache.has(tintHex)) return impostorCache.get(tintHex)!;

  const template = await loadGLB();
  if (!template) return null;

  const model = template.clone(true);

  // ---- Scene ----
  const scene = new THREE.Scene();
  // Transparent background so the sprite composites nicely in PixiJS
  scene.background = null;

  // ---- Auto-center & scale ----
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const scale = 1.8 / maxDim;
  model.position.sub(center);
  model.scale.setScalar(scale);
  scene.add(model);

  // ---- Camera (3/4 top-down isometric-ish view) ----
  const camera = new THREE.PerspectiveCamera(40, 1, 0.01, 100);
  camera.position.set(1.8, 1.2, 2.5);
  camera.lookAt(0, 0, 0);

  // ---- Lights ----
  scene.add(new THREE.AmbientLight(0xffffff, 0.6));

  const key = new THREE.DirectionalLight(0xffffff, 1.0);
  key.position.set(4, 3, 5);
  scene.add(key);

  const fill = new THREE.DirectionalLight(0x4488aa, 0.3);
  fill.position.set(-3, -1, -3);
  scene.add(fill);

  // Tinted rim light
  const rimColor = new THREE.Color(`#${tintHex}`);
  const rim = new THREE.DirectionalLight(rimColor, 0.5);
  rim.position.set(0, -2, -4);
  scene.add(rim);

  // ---- Renderer ----
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  renderer.setSize(IMPOSTOR_SIZE, IMPOSTOR_SIZE);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;

  renderer.render(scene, camera);

  // Copy to a regular canvas so we can dispose the GL context
  const canvas = document.createElement('canvas');
  canvas.width = IMPOSTOR_SIZE;
  canvas.height = IMPOSTOR_SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(renderer.domElement, 0, 0);

  renderer.dispose();

  impostorCache.set(tintHex, canvas);
  return canvas;
}

/**
 * Get a PixiJS Texture from the baked impostor.
 * Must be called after `bakeStationImpostor()`.
 */
export function getStationImpostorTexture(tintHex: string = '20a5a6'): PixiTexture | null {
  if (pixiTextureCache.has(tintHex)) return pixiTextureCache.get(tintHex)!;

  const canvas = impostorCache.get(tintHex);
  if (!canvas) return null;

  const texture = PixiTexture.from(canvas);
  pixiTextureCache.set(tintHex, texture);
  return texture;
}

/**
 * Convenience: bake + return PixiJS texture in one call.
 */
export async function bakeAndGetStationTexture(tintHex: string = '20a5a6'): Promise<PixiTexture | null> {
  await bakeStationImpostor(tintHex);
  return getStationImpostorTexture(tintHex);
}

/**
 * Check if the impostor for the default tint is already baked.
 */
export function hasStationImpostorCached(tintHex: string = '20a5a6'): boolean {
  return impostorCache.has(tintHex);
}
